import React, { useEffect, useState } from 'react';
import CMSService from '../../services/CMSService';
import { FooterConfig } from '../../types';

/**
 * Footer Component for Landing Page
 * Displays footer links, contact info, and social media
 */
const Footer: React.FC = () => {
  const [footerData, setFooterData] = useState<FooterConfig | null>(null);

  useEffect(() => {
    const loadFooterData = async () => {
      const response = await CMSService.getFooterConfig();
      if (response.statusCode === 200 && response.data) {
        setFooterData(response.data);
      }
    };

    loadFooterData();
  }, []);

  const defaultFooter: FooterConfig = {
    _id: '',
    company: {
      name: 'IELTS Learning Platform',
      description: 'Prepare for IELTS with our comprehensive learning platform',
      logo: '',
    },
    sections: [
      {
        title: 'Quick Links',
        links: [
          { label: 'Home', url: '/' },
          { label: 'Courses', url: '/courses' },
          { label: 'Blog', url: '/blog' },
        ],
        order: 1,
      },
      {
        title: 'Company',
        links: [
          { label: 'About Us', url: '/about' },
          { label: 'Contact', url: '/contact' },
          { label: 'Privacy Policy', url: '/privacy' },
        ],
        order: 2,
      },
    ],
    socialLinks: [
      { platform: 'facebook', url: '#' },
      { platform: 'instagram', url: '#' },
      { platform: 'twitter', url: '#' },
    ],
    contact: {
      email: 'info@ielts.com',
      phone: '+1 234 567 8900',
      address: '123 Main St, City, Country',
    },
    copyright: '© 2024 IELTS Learning Platform. All rights reserved.',
    updatedAt: new Date(),
  };

  const data = footerData || defaultFooter;
  const company = data?.company || defaultFooter.company;

  return (
    <footer className="bg-gray-900 text-gray-300 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Footer Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div>
            <h3 className="text-white font-bold text-lg mb-4">{company.name}</h3>
            <p className="text-sm text-gray-400">{company.description}</p>
          </div>

          {/* Footer Sections */}
          {data.sections &&
            data.sections
              .sort((a, b) => a.order - b.order)
              .map((section) => (
                <div key={section.title}>
                  <h4 className="text-white font-semibold mb-4">{section.title}</h4>
                  <ul className="space-y-2">
                    {section.links.map((link) => (
                      <li key={link.url}>
                        <a
                          href={link.url}
                          className="text-gray-400 hover:text-white transition-colors text-sm"
                        >
                          {link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact Us</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              {data.contact?.email && (
                <li>
                  <i className="fas fa-envelope mr-2"></i>
                  <a href={`mailto:${data.contact.email}`} className="hover:text-white">
                    {data.contact.email}
                  </a>
                </li>
              )}
              {data.contact?.phone && (
                <li>
                  <i className="fas fa-phone mr-2"></i>
                  <a href={`tel:${data.contact.phone}`} className="hover:text-white">
                    {data.contact.phone}
                  </a>
                </li>
              )}
              {data.contact?.address && (
                <li>
                  <i className="fas fa-map-marker-alt mr-2"></i>
                  <span>{data.contact.address}</span>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Divider */}
        <hr className="border-gray-700 mb-8" />

        {/* Bottom Footer */}
        <div className="flex flex-col md:flex-row justify-between items-center">
          {/* Copyright */}
          <p className="text-sm text-gray-500 text-center md:text-left mb-4 md:mb-0">
            {data.copyright || defaultFooter.copyright}
          </p>

          {/* Social Links */}
          {data.socialLinks && data.socialLinks.length > 0 && (
            <div className="flex gap-4">
              {data.socialLinks.map((social) => (
                <a
                  key={social.platform}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <i className={`fab fa-${social.platform} text-xl`}></i>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
